// Where to send the user after login. appState comes back through the Auth0 redirect, so it is treated
// as untrusted: only same-origin relative paths ("/reports?type=nfc&report=...") are accepted.
const MAX_DECODE_ROUNDS = 3;
const MAX_LENGTH = 2048;
const BASE = 'http://return-path.invalid';
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f]/;

// A pathname candidate: exactly one leading "/", no backslash or control character (browsers read "\\" as "/"),
// and it must resolve on our own origin without turning into a "//host" path.
const isSafePath = (candidate) => {
  if (!candidate.startsWith('/') || candidate.startsWith('//') || candidate.includes('\\') || CONTROL.test(candidate)) return false;
  try {
    const url = new URL(candidate, BASE);
    return url.origin === BASE && !url.pathname.startsWith('//');
  } catch (e) {
    return false;
  }
};

// Only the pathname is checked structurally, and after the same decoding history v4 applies to it (decodeURI)
// plus repeated decodeURIComponent: query and hash are not path, so a literal "%" or "\\" there is harmless
// ("?q=100%", "?q=C%3A%5CUsers"). Anything the pathname decoding cannot handle is refused.
export const sanitizeReturnPath = (value) => {
  if (typeof value !== 'string' || value.length > MAX_LENGTH || CONTROL.test(value)) return null;
  const cut = value.search(/[?#]/);
  const pathname = cut === -1 ? value : value.slice(0, cut);
  try {
    if (!isSafePath(pathname)) return null;
    // history's own decodeURI first (it leaves %2F alone), then whatever a router or server adds on top.
    let candidate = decodeURI(pathname);
    for (let round = 0; round <= MAX_DECODE_ROUNDS; round += 1) {
      if (!isSafePath(candidate)) return null;
      const decoded = decodeURIComponent(candidate);
      if (decoded === candidate) return value;
      candidate = decoded;
    }
  } catch (e) {
    return null;
  }
  return null;
};

// The current page as a return path, without the Auth0 callback parameters.
export const currentReturnPath = () => {
  const {pathname, search} = window.location;
  const clean = /[?&](code|state|error)=/.test(search) ? '' : search;
  return sanitizeReturnPath(pathname + clean) || '/';
};

// A redirectUrl passed in the address (Shoper, PhoneHQ and mobile app logins): a safe same-origin path, or a full
// URL on our own origin, turned into its path. Anything else (another site, javascript:) gives null.
export const sanitizeRedirectUrl = (value, origin = window.location.origin) => {
  if (typeof value !== 'string') return null;
  if (value.startsWith('/')) return sanitizeReturnPath(value);
  try {
    const url = new URL(value);
    return url.origin === origin ? sanitizeReturnPath(url.pathname + url.search + url.hash) : null;
  } catch (e) {
    return null;
  }
};
